import mime from 'mime/lite';
import { ReactNode } from 'react';

import { PlaygroundOutputCodeBlock } from '@/Pages/Playground/components/PlaygroundOutputCodeBlock';
import { Tooltip } from '@/design-system';
import {
  MessageContentPart,
  S3DataSchema,
  isMessageContentPartAnthropicMultimodal,
  isMessageContentPartAnthropicToolResult,
  isMessageContentPartLangChainMultimodal,
  isMessageContentPartOpenAIAudio,
  isMessageContentPartOpenAIFile,
  isMessageContentPartOpenAIImageGenerationContent,
  isMessageContentPartOpenAIReasoningContent,
  isMessageContentPartTool,
  isMessageContentPartVertexAIMultimodal,
  zAnthropicCitation,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { ExpandableTraceInformation } from '../ExpandableTraceInformation';
import {
  OpenAIFunctionCallKwargs,
  OpenAIFunctionCallKwargsSchema,
  OpenAIToolCallsKwargs,
  OpenAIToolCallsKwargsSchema,
  ToolCallsKwargs,
  ToolCallsKwargsSchema,
} from '../Message.utils';
import { ToolCallsList } from '../MessageToolCalls/ToolCallsList';
import { TextWithLinks } from '../TextWithLinks';
import { MultimodalContent } from './MultimodalContent';
import {
  CREATE_DATA_URL_PREFIX_FROM_MIME_TYPE,
  DATA_PREFIX,
} from './constants';
import { getMimeTypeFromDataUrl } from './utils/RichTextEditor.utils';

// Consolidated into ToolCallsList

export function RenderToolCalls({
  toolCall,
  fallback,
}: {
  toolCall?: OpenAIFunctionCallKwargs | OpenAIToolCallsKwargs | ToolCallsKwargs;
  fallback?: unknown;
}) {
  const fnCall = OpenAIFunctionCallKwargsSchema.safeParse(toolCall);

  if (fnCall.success) {
    return (
      <div className="my-1 rounded-md border border-secondary">
        <ToolCallsList
          items={[
            {
              name: fnCall.data.function_call.name,
              args: fnCall.data.function_call.arguments,
            },
          ]}
          editable={false}
        />
      </div>
    );
  }

  const oaiToolCalls = OpenAIToolCallsKwargsSchema.safeParse(toolCall);
  if (oaiToolCalls.success) {
    return (
      <div className="my-1 rounded-md border border-secondary">
        <ToolCallsList
          items={oaiToolCalls.data.tool_calls.map((toolCall) => ({
            name: toolCall.function.name,
            args: toolCall.function.arguments,
            id: toolCall.id,
          }))}
          editable={false}
        />
      </div>
    );
  }

  const toolCalls = ToolCallsKwargsSchema.safeParse(toolCall);
  if (toolCalls.success && toolCalls.data.tool_calls.length > 0) {
    return (
      <div className="my-1 rounded-md border border-secondary">
        <ToolCallsList
          items={toolCalls.data.tool_calls.map((toolCall) => ({
            name: toolCall.name,
            id: toolCall.id,
            args: toolCall.args,
          }))}
          editable={false}
        />
      </div>
    );
  }

  return (
    <PlaygroundOutputCodeBlock
      code={JSON.stringify(toolCall ?? fallback, null, 2)}
      language={'json'}
    />
  );
}

interface ContentPartRendererProps {
  part: MessageContentPart | string;
  idx: number;
  attachments?: Record<string, S3DataSchema>;
  className?: string;
  showToolCalls?: boolean;
  toolCalls?: unknown[];
}

// Use zod schema parsing helpers instead of ad-hoc guards
function tryParseOAITool(value: unknown) {
  const res = OpenAIToolCallsKwargsSchema.safeParse({ tool_calls: [value] });
  return res.success ? res.data.tool_calls[0] : null;
}

function tryParseGenericTool(value: unknown) {
  const res = ToolCallsKwargsSchema.safeParse({ tool_calls: [value] });
  return res.success ? res.data.tool_calls[0] : null;
}

function getFallbackArgs(
  value: unknown
): string | Record<string, unknown> | undefined {
  if (typeof value === 'object' && value != null) {
    const v = value as Record<string, unknown>;
    if ('input' in v) return v['input'] as string | Record<string, unknown>;
    if ('args' in v) return v['args'] as string | Record<string, unknown>;
  }
  return undefined;
}

export function ContentPartRenderer({
  part,
  idx,
  attachments,
  className,
  showToolCalls,
  toolCalls,
}: ContentPartRendererProps) {
  if (typeof part === 'string') {
    return <TextWithLinks key={idx} text={part} />;
  }

  if (
    part.type === 'text' ||
    part.type === 'text_delta' ||
    part.type === 'output_text' ||
    part.type === 'input_text'
  ) {
    let content: string | ReactNode = '';
    if (part.text && typeof part.text === 'string') {
      if (
        'citations' in part &&
        Array.isArray(part.citations) &&
        part.citations.every((c) => zAnthropicCitation.safeParse(c).success)
      ) {
        return (
          <span key={idx}>
            <TextWithLinks text={part.text} />
            <Tooltip
              description={JSON.stringify(part.citations, null, 2)}
              side="top"
              align="start"
            >
              <sup className="ml-1 cursor-pointer text-xs text-ls-blue hover:opacity-80">
                [^]
              </sup>
            </Tooltip>
          </span>
        );
      }
      return <TextWithLinks key={idx} text={part.text} />;
    }

    if (part.text && typeof part.text === 'object' && 'value' in part.text) {
      content = part.text.value + '\n';
    }

    return (
      <div key={idx} className={cn('whitespace-pre-wrap text-sm', className)}>
        {content}
      </div>
    );
  }

  if (part.type === 'image_url' || part.type === 'input_image') {
    if (part.image_url == null) {
      return null;
    }
    const url =
      typeof part.image_url === 'string' ? part.image_url : part.image_url.url;
    const urlByAttachment = attachments?.[url]?.presigned_url ?? url;
    const imageDataClasses =
      'max-h-[45vh] w-full rounded-lg border border-secondary bg-secondary-hover object-contain object-center';
    if (typeof urlByAttachment !== 'string') {
      return (
        <img
          key={idx}
          src={`data:image/png;base64,`}
          className={imageDataClasses}
        />
      );
    }
    // Check if the URL is a base64 string
    const isBase64 =
      urlByAttachment.startsWith('data:') ||
      /^[A-Za-z0-9+/=]+$/.test(urlByAttachment);
    const srcUrl =
      isBase64 && !urlByAttachment.startsWith('data:')
        ? `data:image/png;base64,${urlByAttachment}` // Adjust image type if needed
        : urlByAttachment;
    return <img key={idx} src={srcUrl} className={imageDataClasses} />;
  } else if (isMessageContentPartLangChainMultimodal(part)) {
    return (
      <MultimodalContent
        key={idx}
        contentType={part.mime_type}
        url={part.data ?? part.url}
        contentName={''}
        unstyled={true}
      />
    );
  } else if (isMessageContentPartOpenAIFile(part)) {
    const url = part.file.file_data;
    if (typeof url !== 'string') {
      return null;
    }
    let mimeType = mime.getType(part.file.filename);
    if (!mimeType && url?.startsWith(DATA_PREFIX)) {
      mimeType = getMimeTypeFromDataUrl(url);
    }
    if (!mimeType) {
      return null;
    }
    return (
      <MultimodalContent
        key={idx}
        contentType={mimeType}
        url={url}
        contentName={part.file.filename}
        unstyled={true}
      />
    );
  } else if (isMessageContentPartOpenAIAudio(part)) {
    const url = part.input_audio.data;
    if (typeof url !== 'string') {
      return null;
    }
    const mimeType = `audio/${part.input_audio.format}`;
    return (
      <MultimodalContent
        key={idx}
        contentType={mimeType}
        url={url}
        contentName={`audio.${part.input_audio.format}`}
        unstyled={true}
      />
    );
  } else if (isMessageContentPartOpenAIReasoningContent(part)) {
    if (!part.summary?.length) return null;
    return (
      <ExpandableTraceInformation
        content={part.summary.join('\n')}
        header="Reasoning"
      />
    );
  } else if (isMessageContentPartOpenAIImageGenerationContent(part)) {
    const mimeType = `image/${part.output_format ?? 'png'}`;
    const url = `${CREATE_DATA_URL_PREFIX_FROM_MIME_TYPE(mimeType)}${
      part.result
    }`;
    return (
      <div className="flex flex-col gap-2">
        <div className="rounded-md border border-secondary">
          <ToolCallsList
            items={[
              {
                name: 'image_generation',
                args: {
                  size: part.size,
                  quality: part.quality,
                  background: part.background,
                },
                id: part.id,
              },
            ]}
            editable={false}
          />
        </div>
        <ExpandableTraceInformation
          content={part.revised_prompt ?? ''}
          header="Revised prompt"
          className="p-0"
        />
        <MultimodalContent
          key={idx}
          contentType={mimeType}
          url={url}
          contentName={part.id}
          unstyled={true}
        />
      </div>
    );
  } else if (isMessageContentPartAnthropicMultimodal(part)) {
    const mimeType = 'media_type' in part.source && part.source.media_type;
    if (mimeType) {
      const url = 'data' in part.source && part.source.data;
      if (typeof url !== 'string') {
        return null;
      }
      return (
        <MultimodalContent
          key={idx}
          contentType={mimeType}
          url={url}
          contentName={('title' in part && part.title) || part.type}
          unstyled={true}
        />
      );
    }
    if (part.type === 'image' && part.source.type === 'url') {
      return (
        <MultimodalContent
          key={idx}
          contentType={part.type}
          url={part.source.url}
          contentName={part.type}
          unstyled={true}
        />
      );
    }
  } else if (isMessageContentPartVertexAIMultimodal(part)) {
    // Handle VertexAI (Gemini) multimodal content
    if (part.type === 'media') {
      return (
        <MultimodalContent
          key={idx}
          contentType={part.mime_type}
          url={part.data}
          contentName="media"
          unstyled={true}
        />
      );
    }
    // For text parts, return null as they're handled elsewhere
    return null;
  } else if (isMessageContentPartTool(part) && showToolCalls) {
    const foundOAI = toolCalls
      ?.map((t) => tryParseOAITool(t))
      .find((t) => t && t.id === part.id);
    const foundGeneric = !foundOAI
      ? toolCalls
          ?.map((t) => tryParseGenericTool(t))
          .find((t) => t && t.id === part.id)
      : null;

    const toolCall = foundOAI
      ? { tool_calls: [foundOAI] }
      : foundGeneric
      ? { tool_calls: [foundGeneric] }
      : ({
          tool_calls: [
            {
              id: part.id,
              name: part.name,
              args: (getFallbackArgs(part) ?? {}) as Record<string, unknown>,
            },
          ],
        } satisfies ToolCallsKwargs);

    return <RenderToolCalls toolCall={toolCall} fallback={part} />;
  } else if (isMessageContentPartAnthropicToolResult(part) && showToolCalls) {
    return (
      <RenderToolCalls
        toolCall={{
          tool_calls: [
            {
              id: part.tool_use_id,
              name: part.type,
              args: { content: part.content },
            },
          ],
        }}
        fallback={part}
      />
    );
  }

  return null;
}
